/* eslint-disable @typescript-eslint/consistent-type-imports */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable new-cap */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import {IConvesioPayContext, PaymentResult, ConvesioPayPaymentRequest, ConvesioPaySubscriptionRequest, ConvesioPayRecurringRequest} from '../../models/convesiopay';
import {registerGatewayBatch} from '../../reducers/paymentConfigurationReducer';
import {store} from '../../store';
import {type GatewayConfiguration} from '../../models/GatewayConfiguration';
import {getLocaleText} from '../../util/translation';
import {DefaultCart} from '../../reducers/cartReducer';
import {FeatureFlag} from '../../../../@type/features';
import {Feature} from '../../reducers/environmentReducer';
import {MerchantConfiguration} from '../../reducers/merchantConfigurationReducer';
import {PeachPayOrder} from '../../reducers/peachPayOrderReducer';
import {setupConvesioPayButton} from './button';
import {displayPaymentErrorMessage, type OrderService} from '../order';
import {startModalProcessing, stopModalLoading} from '../../reducers/environmentReducer';
import {getErrorString} from '../../../../@shared/ts/error';
import {$qsAll} from '../../../../@shared/ts/dom';
// Using generic card badge for now - can be replaced with ConvesioPay specific logo later
import cardBadgeURL from '../../../img/badge/card.svg';

declare global {
	interface Window {
		ConvesioPay: any;
	}
}

class ConvesioPayGateway {
	private cpay: any;
	private component: any;
	private paymentToken = '';

	async initialize(config: IConvesioPayContext['config']): Promise<void> {
		// Load ConvesioPay script dynamically (like GiveWP)
		await this.loadConvesioPayScript();

		// Initialize ConvesioPay (like GiveWP)
		this.cpay = window.ConvesioPay(config.apiKey);
		this.component = this.cpay.component({
			environment: config.environment,
			clientKey: config.clientKey,
		});
	}

	async loadConvesioPayScript(): Promise<void> {
		return new Promise((resolve, reject) => {
			// Check if script already exists (like GiveWP)
			const existingScript = document.querySelector('script[src="https://js.convesiopay.com/v1/"]');
			if (existingScript) {
				resolve();
				return;
			}

			const script = document.createElement('script');
			script.src = 'https://js.convesiopay.com/v1/';
			script.async = true;
			script.onload = () => {
				resolve();
			};

			script.onerror = () => {
				reject(new Error('Failed to load ConvesioPay script'));
			};

			document.head.appendChild(script);
		});
	}

	async mountPaymentForm(selector: string): Promise<void> {
		if (!this.component) {
			throw new Error('ConvesioPay component not initialized');
		}

		// Mount component (like GiveWP)
		this.component.mount(selector);

		// Set up change listener (like GiveWP)
		this.component.on('change', async (event: any) => {
			if (event.isValid) {
				try {
					const token = await this.component.createToken();
					if (token) {
						this.paymentToken = token;
					}
				} catch (error) {
					// Error creating token
				}
			}
		});
	}

	async createToken(): Promise<string> {
		if (!this.component) {
			throw new Error('ConvesioPay component not initialized');
		}

		if (!this.paymentToken) {
			throw new Error('No payment token available. Please complete the form first.');
		}

		return this.paymentToken;
	}

	async createPayment(request: ConvesioPayPaymentRequest): Promise<PaymentResult> {
		try {
			// Direct API call like GiveWP
			const response = await fetch('/api/v1/convesiopay/payments', {
				method: 'POST',
				headers: {'Content-Type': 'application/json'},
				body: JSON.stringify(request),
			});

			const result = await response.json();

			if (result.success) {
				return {
					success: true,
					paymentId: result.data.id,
					status: result.data.status,
				};
			}

			return {
				success: false,
				error: result.error,
			};
		} catch (error) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Payment failed',
			};
		}
	}

	async createSubscriptionPayment(request: ConvesioPaySubscriptionRequest): Promise<PaymentResult> {
		try {
			// Direct API call like GiveWP
			const response = await fetch('/api/v1/convesiopay/subscriptions/initial', {
				method: 'POST',
				headers: {'Content-Type': 'application/json'},
				body: JSON.stringify(request),
			});

			const result = await response.json();

			if (result.success) {
				return {
					success: true,
					paymentId: result.data.id,
					status: result.data.status,
				};
			}

			return {
				success: false,
				error: result.error,
			};
		} catch (error) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Subscription payment failed',
			};
		}
	}

	async processRecurringPayment(request: ConvesioPayRecurringRequest): Promise<PaymentResult> {
		try {
			// Direct API call like GiveWP
			const response = await fetch(`/api/v1/convesiopay/subscriptions/${request.customerId}/process`, {
				method: 'POST',
				headers: {'Content-Type': 'application/json'},
				body: JSON.stringify({
					customerId: request.customerId,
					amount: request.amount,
					orderDetails: request.orderDetails,
				}),
			});

			const result = await response.json();

			if (result.success) {
				return {
					success: true,
					paymentId: result.data.id,
					status: result.data.status,
				};
			}

			return {
				success: false,
				error: result.error,
			};
		} catch (error) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Recurring payment failed',
			};
		}
	}
}

export const convesioPayGateway = new ConvesioPayGateway();

// Module-level mount tracking variables
let isConvesioPayComponentMounted = false;
let mountedConvesioPayComponent: any = null;
let mountedComponentTarget: string | null = null;

export async function initConvesioPayPaymentIntegration(orderService: OrderService): Promise<void> {
	try {
		// Register the gateway first so it appears in the Express Checkout UI
		await registerConvesioPayGateways();

		// Get configuration from backend
		// Use WordPress REST API endpoint - should be passed from PHP via wp_localize_script
		const restUrl = (window as any).peachpayConvesioPay?.restUrl || '/wp-json/peachpay/v1/convesiopay/config';
		const response = await fetch(restUrl);
		const result = await response.json();

		if (result.success) {
			await convesioPayGateway.initialize(result.data);
		}

		// Set up button click handler
		setupConvesioPayButtonClickHandler(orderService);
	} catch (error) {
		// Error initializing ConvesioPay
	}
}

export async function registerConvesioPayGateways(): Promise<void> {
	// Get active methods from Feature.metadata (primary source for Express Checkout)
	// Fallback to window.peachpay_convesiopay_unified_data if Feature.metadata is not available
	let activeMethods: string[] = [];

	try {
		activeMethods = Feature.metadata<string[]>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'active_methods') ?? [];
	} catch (error) {
		// Fallback to window data if Feature.metadata fails
		const settings = (window as any).peachpay_convesiopay_unified_data ?? {};
		activeMethods = settings.active_methods ?? [];
	}

	// Only register if at least one payment method is active
	if (activeMethods.length === 0) {
		return; // Don't register gateway if no methods are active
	}

	// Register ConvesioPay Unified Gateway with the payment system
	const gatewayConfigurations: Record<string, GatewayConfiguration> = {};

	// Register the unified gateway (handles ApplePay, BTCPay, and Card)
	gatewayConfigurations['peachpay_convesiopay_unified'] = {
		gatewayId: 'peachpay_convesiopay_unified',
		name: getLocaleText('ConvesioPay'),
		description: '',
		assets: {
			badge: {src: cardBadgeURL},
		},
		browser: true, // Supported in all browsers
		initialized: true, // Will be set to false if initialization fails
	};

	store.dispatch(registerGatewayBatch(gatewayConfigurations));

	// Setup the ConvesioPay button display
	setupConvesioPayButton();

	// Track previous gateway to prevent re-mounting on every store update
	let previousSelectedGateway: string | null = null;
	let isMounting = false;

	// Listen for when this gateway is selected to mount the component
	store.subscribe(() => {
		const {selectedGateway} = store.getState().paymentConfiguration;

		// Skip if gateway hasn't changed
		if (previousSelectedGateway === selectedGateway) {
			return;
		}

		// If switching away from ConvesioPay, unmount
		if (previousSelectedGateway === 'peachpay_convesiopay_unified' && selectedGateway !== 'peachpay_convesiopay_unified') {
			unmountConvesioPayComponent();
			previousSelectedGateway = selectedGateway;
			return;
		}

		// Only mount if switching TO ConvesioPay and not already mounting
		if (selectedGateway === 'peachpay_convesiopay_unified' && !isMounting) {
			previousSelectedGateway = selectedGateway;
			isMounting = true;

			mountConvesioPayComponent()
				.catch(() => {
					// Failed to mount ConvesioPay component
				})
				.finally(() => {
					isMounting = false;
				});
		} else {
			previousSelectedGateway = selectedGateway;
		}
	});
}

/**
 * Get order data for ConvesioPay (following blocks.js pattern)
 */
// eslint-disable-next-line complexity
async function getOrderDataForConvesioPay() {
	const orderData: {
		orderNumber: string;
		amount: number;
		currency: string;
		email: string;
		name: string;
		billingAddress?: {
			street: string;
			city: string;
			state: string;
			postalCode: string;
			country: string;
		};
		shippingAddress?: {
			street: string;
			city: string;
			state: string;
			postalCode: string;
			country: string;
		};
	} = {
		orderNumber: `SESSION-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
		amount: Math.round(DefaultCart.total() * 100), // Convert to cents
		currency: MerchantConfiguration.currency.configuration().code ?? 'USD',
		email: 'customer@example.com',
		name: 'Customer',
	};

	// Try to get customer data from PeachPay order reducer
	try {
		const formData = PeachPayOrder.formData();

		const email = formData.get('billing_email') ?? formData.get('email');
		const firstName = formData.get('billing_first_name') ?? formData.get('first_name');
		const lastName = formData.get('billing_last_name') ?? formData.get('last_name');
		const billingStreet = formData.get('billing_address_1') ?? formData.get('billing_street');
		const billingCity = formData.get('billing_city');
		const billingState = formData.get('billing_state');
		const billingPostalCode = formData.get('billing_postcode') ?? formData.get('billing_postal_code');
		const billingCountry = formData.get('billing_country');
		const shippingStreet = formData.get('shipping_address_1') ?? formData.get('shipping_street') ?? billingStreet;
		const shippingCity = formData.get('shipping_city') ?? billingCity;
		const shippingState = formData.get('shipping_state') ?? billingState;
		const shippingPostalCode = formData.get('shipping_postcode') ?? formData.get('shipping_postal_code') ?? billingPostalCode;
		const shippingCountry = formData.get('shipping_country') ?? billingCountry;

		if (email) {
			orderData.email = email.toString();
		}

		if (firstName || lastName) {
			orderData.name = `${firstName ?? ''} ${lastName ?? ''}`.trim() || 'Customer';
		}

		// Add billing address if available
		if (billingStreet || billingCity || billingState || billingPostalCode || billingCountry) {
			orderData.billingAddress = {
				street: (billingStreet ?? '123 Default Street').toString(),
				city: (billingCity ?? 'Default City').toString(),
				state: (billingState ?? 'CA').toString(),
				postalCode: (billingPostalCode ?? '90210').toString(),
				country: (billingCountry ?? 'US').toString(),
			};
		}

		// Add shipping address if available (use billing as fallback)
		if (shippingStreet || shippingCity || shippingState || shippingPostalCode || shippingCountry) {
			orderData.shippingAddress = {
				street: (shippingStreet ?? orderData.billingAddress?.street ?? '123 Default Street').toString(),
				city: (shippingCity ?? orderData.billingAddress?.city ?? 'Default City').toString(),
				state: (shippingState ?? orderData.billingAddress?.state ?? 'CA').toString(),
				postalCode: (shippingPostalCode ?? orderData.billingAddress?.postalCode ?? '90210').toString(),
				country: (shippingCountry ?? orderData.billingAddress?.country ?? 'US').toString(),
			};
		}
	} catch (error) {
		// Fallback - try to get from form fields
		const emailField = document.querySelector('input[type="email"]');
		if (emailField && (emailField as HTMLInputElement).value) {
			orderData.email = (emailField as HTMLInputElement).value;
		}
	}

	// Ensure we have default addresses if not provided
	if (!orderData.billingAddress) {
		orderData.billingAddress = {
			street: '123 Default Street',
			city: 'Default City',
			state: 'CA',
			postalCode: '90210',
			country: 'US',
		};
	}

	if (!orderData.shippingAddress) {
		orderData.shippingAddress = {
			street: orderData.billingAddress.street,
			city: orderData.billingAddress.city,
			state: orderData.billingAddress.state,
			postalCode: orderData.billingAddress.postalCode,
			country: orderData.billingAddress.country,
		};
	}

	return orderData;
}

/**
 * Create and mount ConvesioPay component (following blocks.js createAndMountConvesioPayComponent pattern)
 */
async function createAndMountConvesioPayComponent() {
	try {
		// Check if ConvesioPay SDK is available
		if (typeof (window as any).ConvesioPay === 'undefined') {
			// Load ConvesioPay script if not already loaded
			await convesioPayGateway.loadConvesioPayScript();

			// Wait a bit for SDK to initialize
			await new Promise<void>(resolve => {
				setTimeout(() => {
					resolve();
				}, 100);
			});

			if (typeof (window as any).ConvesioPay === 'undefined') {
				throw new Error('ConvesioPay SDK not available after loading');
			}
		}

		// Get ConvesioPay configuration using Feature.metadata (like Stripe does)
		const apiKey = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'api_key');
		const clientKey = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'client_key');
		const apiUrl = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'api_url') ?? 'https://api.convesiopay.com';
		const integrationName = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'integration_name');
		const environment = apiUrl.includes('qa') ? 'test' : 'live';

		if (!apiKey || !clientKey) {
			throw new Error('ConvesioPay API key or Client key not found in config');
		}

		// Get order data for customer email and other details
		const orderData = await getOrderDataForConvesioPay();

		// Create ConvesioPay instance
		const cpay = (window as any).ConvesioPay(apiKey);

		// Create component with all options (following blocks.js pattern)
		const component = cpay.component({
			environment,
			clientKey,
			customerEmail: orderData.email,
			integration: integrationName ?? window.location.hostname,
			theme: 'light',
		});

		// Set up event listeners (following blocks.js pattern)
		// eslint-disable-next-line complexity
		component.on('change', async (event: any) => {
			try {
				// Handle ApplePay payments with auto-submit when successful
				if (event.type === 'applepay') {
					// Check for errors first - only show error if there are actual errors AND payment is not successful
					// This matches blocks.js pattern exactly: check isSuccessful first, then errors
					if (event.isSuccessful) {
						// ApplePay payment intent was successfully authorized
						void handleApplePayPaymentSuccess(component, event);
					} else if (event.errors && event.errors.length > 0) {
						// Handle ApplePay errors - extract detailed error message
						let errorMessage = 'ApplePay payment failed';

						// Try to extract error message from different possible locations
						if (event.error?.data?.body?.message) {
							errorMessage = event.error.data.body.message;
						} else if (event.error?.message) {
							errorMessage = event.error.message;
						} else if (typeof event.errors[0] === 'string') {
							errorMessage = event.errors[0];
						} else if (event.errors[0]?.message) {
							errorMessage = event.errors[0].message;
						}

						// Include status code if available
						if (event.error?.status) {
							errorMessage = `[${event.error.status}] ${errorMessage}`;
						}

						displayPaymentErrorMessage(errorMessage);
					} else if (event.paymentData || event.paymentId || event.paymentData?.paymentId) {
						// Payment data exists even if isSuccessful isn't explicitly true - treat as success
						// This handles cases where the event structure might be slightly different
						void handleApplePayPaymentSuccess(component, event);
					}

					return;
				}

				// Handle BTCPay payments
				if (event.type === 'btcpay') {
					// BTCPay is handled via postMessage listener
					return;
				}

				// Handle card payments (default case - card or no type specified)
				if (event.type === 'card' || !event.type || event.type === '') {
					if (event.isValid) {
						// Component is valid, try to create token
						if (typeof component.createToken === 'function') {
							const tokenResult = component.createToken();
							const token = tokenResult && typeof tokenResult.then === 'function'
								? await tokenResult
								: tokenResult;

							if (token) {
								(window as any).convesiopayPaymentToken = token;
							}
						}
					}

					// Don't process ApplePay checks for card events
					return;
				}

				// Also check for ApplePay by payment method and success flags (only for non-card events)
				// This is a fallback check in case event.type wasn't set correctly
				if (event.isValid === true && event.isSuccessful === true && event.type !== 'card' && event.type !== 'btcpay') {
					// Try to detect payment method
					if (event.paymentData?.paymentMethod === 'applepay'
						|| event.paymentMethod === 'applepay'
						|| event.type?.includes('applepay')) {
						void handleApplePayPaymentSuccess(component, event);
					}
				}
			} catch (error) {
				// Error handling component change
			}
		});

		return {success: true, component};
	} catch (error) {
		return {
			success: false,
			message: error instanceof Error ? error.message : 'Failed to create component',
		};
	}
}

/**
 * Create BTCPay session via AJAX call (following blocks.js pattern)
 */
async function createBTCPaySession(orderData: Awaited<ReturnType<typeof getOrderDataForConvesioPay>>): Promise<{success: boolean; session?: string; message?: string}> {
	try {
		// Get AJAX URL and nonce
		const ajaxURL = Feature.metadata<string>(FeatureFlag.EXPRESS_CHECKOUT, 'admin_ajax_url') ?? '/wp-admin/admin-ajax.php';

		// Get nonce from ConvesioPay feature metadata (preferred), or fallback to global config
		let nonce = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'nonce') ?? '';

		if (!nonce && typeof (window as any).peachpayConvesioPayAjax !== 'undefined') {
			nonce = (window as any).peachpayConvesioPayAjax.nonce ?? '';
		}

		// Get integration_name from Feature.metadata (same as component and ApplePay)
		const integrationName = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'integration_name') ?? window.location.hostname;

		const requestData = {
			action: 'peachpay_convesiopay_create_btcpay_session',
			nonce,
			integration: integrationName,
			returnUrl: window.location.href,
			orderNumber: orderData.orderNumber,
			amount: orderData.amount.toString(),
			currency: orderData.currency,
			email: orderData.email,
			name: orderData.name,
			// Include required address fields for ConvesioPay API
			billingAddress: JSON.stringify(orderData.billingAddress ?? {}),
			shippingAddress: JSON.stringify(orderData.shippingAddress ?? {}),
		};

		const response = await fetch(ajaxURL, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body: new URLSearchParams(requestData as any).toString(),
		});

		if (!response.ok) {
			const errorText = await response.text();
			throw new Error(`HTTP ${response.status}: ${errorText}`);
		}

		const sessionResponse = await response.json();

		if (!sessionResponse.success || !sessionResponse.data?.session) {
			const errorMessage = sessionResponse.message ?? sessionResponse.data?.message ?? 'Failed to create BTCPay session';
			throw new Error(errorMessage);
		}

		return {success: true, session: sessionResponse.data.session};
	} catch (error) {
		return {
			success: false,
			message: error instanceof Error ? error.message : 'Failed to create BTCPay session',
		};
	}
}

async function mountConvesioPayComponent(): Promise<void> {
	// Early return if already mounted
	if (isConvesioPayComponentMounted && mountedConvesioPayComponent) {
		return;
	}

	// Find the correct container (following Stripe pattern)
	const mountSelector = '#pp-pms-new div.pp-pm-saved-option[data-gateway="peachpay_convesiopay_unified"]';
	const container = document.querySelector(mountSelector);

	if (!container) {
		return;
	}

	try {
		// Clear the container first (following Stripe pattern)
		const containers = document.querySelectorAll('div.pp-pm-saved-option[data-gateway="peachpay_convesiopay_unified"]');
		containers.forEach(($el: Element) => {
			($el as HTMLElement).innerHTML = '';
		});

		// Get order data for payment sessions
		const orderData = await getOrderDataForConvesioPay();

		// Create BTCPay session first (following blocks.js pattern)
		let btcPaySession: string | null = null;
		try {
			const sessionResult = await createBTCPaySession(orderData);
			if (sessionResult.success && sessionResult.session) {
				btcPaySession = sessionResult.session;
				// Store session globally so it can be used when placing the order
				(window as any).btcPaySession = btcPaySession;
			}
		} catch (sessionError) {
			// BTCPay session creation error (continuing without session)
		}

		// Create and mount the component (following blocks.js pattern)
		const componentResult = await createAndMountConvesioPayComponent();

		if (!componentResult.success || !componentResult.component) {
			throw new Error(componentResult.message ?? 'Failed to create component');
		}

		const {component} = componentResult;

		// Mount the component to the correct selector (following Stripe pattern)
		await component.mount(mountSelector);

		// Update mount tracking variables
		isConvesioPayComponentMounted = true;
		mountedConvesioPayComponent = component;
		mountedComponentTarget = mountSelector;

		// Get settings for integration name (following blocks.js pattern exactly)
		const settings = (window as any).peachpay_convesiopay_unified_data ?? {};

		// Get integration_name from Feature.metadata (preferred) or fallback to settings
		const integrationName = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'integration_name') ?? settings.integration_name;

		// Create ApplePay session (NEW FLOW - no orderNumber)
		try {
			// Create ApplePay session WITHOUT orderNumber to use new token-based flow
			// This matches the card payment flow where payment is created AFTER order exists
			// Note: billingAddress/shippingAddress are NOT included in ApplePay session creation
			// They are only used for BTCPay intent creation
			const applePaySessionResult = await component.createApplePaySession({
				integration: integrationName,
				returnUrl: window.location.href,
				// orderNumber: REMOVED - new flow creates payment after order exists
				amount: orderData.amount,
				currency: orderData.currency,
				email: orderData.email,
				name: orderData.name,
			});

			// Note: createApplePaySession may return undefined - the SDK manages the session internally
			if (applePaySessionResult !== undefined) {
				// Store session for reference if a value is returned
				(window as any).convesiopayApplePaySession = applePaySessionResult;
			}
		} catch (applePayError) {
			// ApplePay session creation failed
		}

		// Create BTCPay intent with session (following blocks.js pattern)
		if (btcPaySession) {
			try {
				const btcPayIntentData = {
					session: btcPaySession,
				};

				await component.createBTCPayIntent(btcPayIntentData);

				// Set up postMessage listener for iframe communication (following blocks.js pattern)
				setupBTCPayMessageListener();
			} catch (btcPayError) {
				// BTCPay intent creation skipped or failed
			}
		}
	} catch (error) {
		// Error mounting ConvesioPay component
		// Reset mount tracking on error
		isConvesioPayComponentMounted = false;
		mountedConvesioPayComponent = null;
		mountedComponentTarget = null;
	}
}

/**
 * Unmount ConvesioPay component when switching away from ConvesioPay gateway
 */
function unmountConvesioPayComponent(): void {
	if (!isConvesioPayComponentMounted || !mountedConvesioPayComponent) {
		return;
	}

	try {
		// Try to unmount using ConvesioPay SDK method if available
		if (typeof mountedConvesioPayComponent.unmount === 'function') {
			mountedConvesioPayComponent.unmount();
		}

		// Clear the container
		if (mountedComponentTarget) {
			const containers = document.querySelectorAll('div.pp-pm-saved-option[data-gateway="peachpay_convesiopay_unified"]');
			containers.forEach(($el: Element) => {
				($el as HTMLElement).innerHTML = '';
			});
		}

		// Remove BTCPay message listener if it exists
		if ((window as any).__convesiopayBTCPayMessageListener) {
			window.removeEventListener('message', (window as any).__convesiopayBTCPayMessageListener);
			delete (window as any).__convesiopayBTCPayMessageListener;
		}

		// Reset mount tracking variables
		isConvesioPayComponentMounted = false;
		mountedConvesioPayComponent = null;
		mountedComponentTarget = null;
	} catch (error) {
		// Error unmounting component
		// Reset tracking even on error
		isConvesioPayComponentMounted = false;
		mountedConvesioPayComponent = null;
		mountedComponentTarget = null;
	}
}

/**
 * Setup BTCPay message listener for iframe communication (following blocks.js pattern)
 * Listens for payment confirmations and automatically clicks the pay button
 */
function setupBTCPayMessageListener(): void {
	// Check if listener already exists to avoid duplicates
	if ((window as any).__convesiopayBTCPayMessageListener) {
		return;
	}

	// eslint-disable-next-line complexity
	const handleMessage = (event: MessageEvent) => {
		// Verify the origin is from BTCPay or ConvesioPay
		if (!event.origin.includes('btcpay') && !event.origin.includes('convesiopay')) {
			// Still check the data in case origin is different but message is valid
			if (event.data && (
				(typeof event.data === 'string' && event.data.includes('Processing'))
				|| (typeof event.data === 'object' && event.data.status === 'Processing')
			)) {
				// Process anyway
			} else {
				return;
			}
		}

		try {
			let messageData: any;

			// Handle both string and object message data
			if (typeof event.data === 'string') {
				try {
					messageData = JSON.parse(event.data);
				} catch (e) {
					messageData = {type: event.data};
				}
			} else {
				messageData = event.data;
			}

			// Check for payment status messages - 'Processing' means payment received
			if (messageData.status === 'Processing') {
				// Extract payment details from the message
				const invoiceId = messageData.invoiceId ?? messageData.paymentId ?? messageData.invoice_id;
				const paymentId = invoiceId ?? messageData.paymentId ?? messageData.id;

				if (!invoiceId && !paymentId) {
					return;
				}

				const paymentData = {
					invoiceId: invoiceId ?? paymentId,
					status: 'Processing',
					paymentId: paymentId ?? invoiceId,
					paymentMethod: 'btcpay',
					amount: messageData.amount ?? 0,
					currency: messageData.currency ?? 'USD',
					orderNumber: messageData.orderNumber ?? messageData.order_number ?? '',
				};

				// Store payment data globally for use in button click handler
				// Format: btc-session-{invoiceId} (matching blocks.js pattern)
				const paymentToken = `btc-session-${invoiceId ?? paymentId}`;
				(window as any).convesiopayBTCPayPaymentData = paymentData;
				(window as any).convesiopayPaymentToken = paymentToken;

				// Verify token is set before auto-clicking
				if ((window as any).convesiopayPaymentToken) {
					// Auto-click the pay button after a short delay to ensure token is ready
					setTimeout(() => {
						autoClickConvesioPayButton();
					}, 500);
				}
			}
		} catch (error) {
			// Error processing BTCPay message
		}
	};

	window.addEventListener('message', handleMessage);
	(window as any).__convesiopayBTCPayMessageListener = true;
}

/**
 * Handle ApplePay payment success with auto-submit (NEW TOKEN-BASED FLOW)
 * After user authorizes payment on device, we receive a token (NOT a paymentId)
 * Backend will create the payment using secret key AFTER WC order is created
 */
async function handleApplePayPaymentSuccess(_component: any, event: any): Promise<void> {
	try {
		// Get order data
		const orderData = await getOrderDataForConvesioPay();

		// NEW FLOW: Extract token from event (SDK provides this after device authorization)
		// Payment does NOT exist yet - it will be created by backend with secret key
		const paymentToken = event.token
			?? event.paymentData?.token
			?? event.data?.token
			?? null;

		if (!paymentToken) {
			displayPaymentErrorMessage('Apple Pay authorization successful but token is missing. Please try again.');
			return;
		}

		// Store ApplePay payment data globally for use in button click handler
		// NOTE: No paymentId yet - that will be created by backend
		const applePayPaymentData = {
			paymentMethod: 'applepay',
			token: paymentToken,
			amount: event.paymentData?.amount ?? orderData.amount,
			currency: event.paymentData?.currency ?? orderData.currency,
			status: 'authorized', // Device authorized, payment will be created by backend
		};

		(window as any).convesiopayApplePayPaymentData = applePayPaymentData;
		(window as any).convesiopayPaymentToken = paymentToken;

		// Auto-click the ConvesioPay payment button after a short delay
		setTimeout(() => {
			autoClickConvesioPayButtonForApplePay();
		}, 500);
	} catch (error) {
		const errorMessage = error instanceof Error ? error.message : 'Apple Pay failed';
		displayPaymentErrorMessage(errorMessage);
	}
}

/**
 * Auto-click the ConvesioPay payment button after ApplePay payment confirmation
 */
function autoClickConvesioPayButtonForApplePay(): void {
	// Verify payment token is set before clicking
	const paymentToken = (window as any).convesiopayPaymentToken;
	const applePayPaymentData = (window as any).convesiopayApplePayPaymentData;

	if (!paymentToken && !applePayPaymentData?.token) {
		return;
	}

	if (!applePayPaymentData || applePayPaymentData.paymentMethod !== 'applepay') {
		return;
	}

	// Find the ConvesioPay pay button
	const payButtonElement = document.querySelector('.convesiopay-btn');
	const payButton = payButtonElement as HTMLButtonElement | null;

	if (!payButton) {
		// Retry after a short delay in case button hasn't rendered yet
		setTimeout(() => {
			const retryButtonElement = document.querySelector('.convesiopay-btn');
			const retryButton = retryButtonElement as HTMLButtonElement | null;
			if (retryButton && (paymentToken ?? applePayPaymentData?.token)) {
				retryButton.click();
			}
		}, 200);
		return;
	}

	if (payButton.disabled || payButton.classList.contains('hide')) {
		// Try to enable the button
		payButton.disabled = false;
		payButton.classList.remove('hide');

		// Wait a bit and try again
		setTimeout(() => {
			if (!payButton.disabled && !payButton.classList.contains('hide') && (paymentToken ?? applePayPaymentData?.token)) {
				payButton.click();
			}
		}, 100);
		return;
	}

	// Trigger a click event on the button
	const clickEvent = new MouseEvent('click', {
		bubbles: true,
		cancelable: true,
		view: window,
	});

	payButton.dispatchEvent(clickEvent);

	// Also try direct click as fallback
	setTimeout(() => {
		if (!payButton.disabled && !payButton.classList.contains('hide') && (paymentToken ?? applePayPaymentData?.token)) {
			payButton.click();
		}
	}, 100);
}

/**
 * Auto-click the ConvesioPay payment button after BTCPay payment confirmation
 */
function autoClickConvesioPayButton(): void {
	// Verify payment token is set before clicking
	const paymentToken = (window as any).convesiopayPaymentToken;
	const btcPayPaymentData = (window as any).convesiopayBTCPayPaymentData;

	if (!paymentToken) {
		return;
	}

	if (!btcPayPaymentData || btcPayPaymentData.paymentMethod !== 'btcpay') {
		return;
	}

	// Find the ConvesioPay pay button
	const payButtonElement = document.querySelector('.convesiopay-btn');
	const payButton = payButtonElement as HTMLButtonElement | null;

	if (!payButton) {
		// Retry after a short delay in case button hasn't rendered yet
		setTimeout(() => {
			const retryButtonElement = document.querySelector('.convesiopay-btn');
			const retryButton = retryButtonElement as HTMLButtonElement | null;
			if (retryButton && paymentToken) {
				retryButton.click();
			}
		}, 200);
		return;
	}

	if (payButton.disabled || payButton.classList.contains('hide')) {
		// Try to enable the button
		payButton.disabled = false;
		payButton.classList.remove('hide');

		// Wait a bit and try again
		setTimeout(() => {
			if (!payButton.disabled && !payButton.classList.contains('hide') && paymentToken) {
				payButton.click();
			}
		}, 100);
		return;
	}

	// Trigger a click event on the button
	// This will trigger the existing click handler we set up earlier
	const clickEvent = new MouseEvent('click', {
		bubbles: true,
		cancelable: true,
		view: window,
	});

	payButton.dispatchEvent(clickEvent);

	// Also try direct click as fallback
	setTimeout(() => {
		if (!payButton.disabled && !payButton.classList.contains('hide') && paymentToken) {
			payButton.click();
		}
	}, 100);
}

export async function activateConvesioPayGateways(): Promise<void> {
	// Activate ConvesioPay gateways
}

export async function paymentFlow(_context: IConvesioPayContext, order: any): Promise<PaymentResult> {
	try {
		// Create payment token (like GiveWP)
		const token = await convesioPayGateway.createToken();

		// Create payment request with billing address (like GiveWP)
		const paymentRequest: ConvesioPayPaymentRequest = {
			paymentToken: token,
			amount: order.total,
			orderDetails: {
				orderNumber: order.id,
				email: order.billing.email,
				name: `${order.billing.first_name} ${order.billing.last_name}`,
				returnUrl: window.location.href,
				billingAddress: {
					houseNumberOrName: order.billing.address_1,
					street: order.billing.address_2,
					city: order.billing.city,
					stateOrProvince: order.billing.state,
					postalCode: order.billing.postcode,
					country: order.billing.country,
				},
			},
		};

		// Process payment
		return await convesioPayGateway.createPayment(paymentRequest);
	} catch (error) {
		return {
			success: false,
			error: error instanceof Error ? error.message : 'Payment flow failed',
		};
	}
}

/**
 * Set up ConvesioPay button click handler (following Stripe pattern)
 */
function setupConvesioPayButtonClickHandler(orderService: OrderService): void {
	// eslint-disable-next-line complexity
	const confirm = async (event: Event): Promise<void> => {
		event.preventDefault();
		event.stopPropagation();

		// Get payment token from component or payment data (BTCPay or ApplePay)
		let paymentToken = (window as any).convesiopayPaymentToken;
		const btcPayPaymentData = (window as any).convesiopayBTCPayPaymentData;
		const applePayPaymentData = (window as any).convesiopayApplePayPaymentData;

		// If BTCPay payment data exists, use it (payment was confirmed via postMessage)
		if (btcPayPaymentData && btcPayPaymentData.paymentMethod === 'btcpay') {
			// Ensure payment token is set for BTCPay (should already be set by setupBTCPayMessageListener)
			const invoiceId = btcPayPaymentData.invoiceId ?? btcPayPaymentData.paymentId;
			if (!paymentToken && invoiceId) {
				// Fallback: create token from BTCPay payment data
				paymentToken = `btc-session-${invoiceId}`;
				(window as any).convesiopayPaymentToken = paymentToken;
			}
		}

		// If ApplePay payment data exists, use it (payment was confirmed via ApplePay event)
		if (applePayPaymentData && applePayPaymentData.paymentMethod === 'applepay') {
		// Use ApplePay token if available (prioritize token from applePayPaymentData)
			if (applePayPaymentData.token) {
				paymentToken = applePayPaymentData.token;
				(window as any).convesiopayPaymentToken = paymentToken;
			}
		}

		// Special handling for BTCPay and ApplePay payments - if we have confirmed payment data,
		// we know payment is confirmed and can proceed even if token format is different
		if (!paymentToken) {
			// Check for BTCPay payment confirmation
			if (btcPayPaymentData && btcPayPaymentData.status === 'Processing') {
				// BTCPay payment confirmed but token missing - create it
				const invoiceId = btcPayPaymentData.invoiceId ?? btcPayPaymentData.paymentId;
				if (invoiceId) {
					paymentToken = `btc-session-${invoiceId}`;
					(window as any).convesiopayPaymentToken = paymentToken;
				} else {
					displayPaymentErrorMessage(getLocaleText('Payment confirmation received but payment details are missing. Please try again.'));
					return;
				}
			} else if (applePayPaymentData && applePayPaymentData.paymentMethod === 'applepay') {
				// Check for ApplePay payment confirmation
				// ApplePay payment confirmed but token missing - use from payment data
				if (applePayPaymentData.token) {
					paymentToken = applePayPaymentData.token;
					(window as any).convesiopayPaymentToken = paymentToken;
				} else {
					displayPaymentErrorMessage(getLocaleText('Payment confirmation received but payment details are missing. Please try again.'));
					return;
				}
			} else {
				// No payment confirmation found
				displayPaymentErrorMessage(getLocaleText('Please complete all required payment fields'));
				return;
			}
		}

		store.dispatch(startModalProcessing());

		// Start transaction
		const {error: transactionError, result: transaction} = await orderService.startTransaction('peachpay_convesiopay_unified');
		if (transactionError || !transaction) {
			const errorMessage = transactionError ? getErrorString(transactionError) : getLocaleText('An unknown error occured while starting the transaction. Please refresh the page and try again.');
			displayPaymentErrorMessage(errorMessage);
			store.dispatch(stopModalLoading());
			return;
		}

		// Place order with payment token
		// Backend expects 'convesiopay_payment_token' (not 'peachpay_convesiopay_payment_token')
		const extraFormData: Record<string, string> = {
			convesiopay_payment_token: paymentToken,
		};

		// For BTCPay payments, add additional required fields
		if (btcPayPaymentData && btcPayPaymentData.paymentMethod === 'btcpay') {
			// Get BTCPay session ID (stored when session was created)
			const btcPaySessionId = (window as any).btcPaySession ?? '';

			// Add BTCPay-specific fields that the backend expects
			if (btcPayPaymentData.invoiceId) {
				extraFormData['btcpay_invoice_id'] = btcPayPaymentData.invoiceId;
				extraFormData['convesiopay_invoice_id'] = btcPayPaymentData.invoiceId;
			}

			if (btcPayPaymentData.paymentId) {
				extraFormData['btcpay_payment_id'] = btcPayPaymentData.paymentId;
				extraFormData['convesiopay_payment_id'] = btcPayPaymentData.paymentId;
			}

			if (btcPayPaymentData.status) {
				extraFormData['btcpay_status'] = btcPayPaymentData.status;
				extraFormData['convesiopay_status'] = btcPayPaymentData.status;
			}

			if (btcPaySessionId) {
				extraFormData['btcpay_session_id'] = btcPaySessionId;
			}
		}

		// For ApplePay payments, add additional required fields (NEW TOKEN-BASED FLOW)
		if (applePayPaymentData && applePayPaymentData.paymentMethod === 'applepay') {
			// Add payment_method_type so unified gateway can detect it correctly
			extraFormData['payment_method_type'] = 'applepay';

			// NEW FLOW: Ensure token is explicitly set for ApplePay (backend needs this to create payment)
			// Priority: applePayPaymentData.token > paymentToken > window.convesiopayPaymentToken
			const applePayToken = applePayPaymentData.token
			|| paymentToken
			|| (window as any).convesiopayPaymentToken
			|| '';
			if (applePayToken) {
				extraFormData['convesiopay_payment_token'] = applePayToken;
				paymentToken = applePayToken; // Update paymentToken variable too
			} else {
				displayPaymentErrorMessage(getLocaleText('Apple Pay token is missing. Please try again.'));
				store.dispatch(stopModalLoading());
				return;
			}

			// NEW FLOW: Send token and order data - backend will create payment with secret key
			// No paymentId at this stage - payment will be created after order exists
			if (applePayPaymentData.amount) {
				extraFormData['applepay_amount'] = applePayPaymentData.amount.toString();
			}

			if (applePayPaymentData.currency) {
				extraFormData['applepay_currency'] = applePayPaymentData.currency;
			}

			if (applePayPaymentData.status) {
				extraFormData['convesiopay_status'] = applePayPaymentData.status;
				extraFormData['status'] = applePayPaymentData.status;
			}

			// Ensure payment method is set correctly
			extraFormData['paymentmethod'] = 'applepay';
		}

		// Final validation: Ensure token is in extraFormData before submitting
		if (!extraFormData['convesiopay_payment_token'] || extraFormData['convesiopay_payment_token'].trim() === '') {
			displayPaymentErrorMessage(getLocaleText('Payment token is missing. Please try again.'));
			store.dispatch(stopModalLoading());
			return;
		}

		const {error: orderError, result: orderResult} = await orderService.placeOrder(transaction, extraFormData);

		if (orderError || !orderResult) {
			const errorMessage = orderError ? getErrorString(orderError) : getLocaleText('An unknown error occured while placing the order. Please refresh the page and try again.');
			displayPaymentErrorMessage(errorMessage);
			store.dispatch(stopModalLoading());
			return;
		}

		// Success - redirect or complete (following Stripe pattern)
		if (orderResult.result === 'success' && 'redirect' in orderResult) {
			const dataURL = new URL(orderResult.redirect);

			if (dataURL.hash === '') {
				await transaction.complete();
				// @ts-expect-error: window.top is always existent
				window.top.location.href = dataURL.toString();
			} else {
				await transaction.complete();
			}
		}
	};

	// Use event delegation to handle dynamically shown/hidden buttons
	// This is more reliable than attaching directly since buttons can be hidden/shown dynamically
	const handleClick = (event: MouseEvent) => {
		const target = event.target as HTMLElement;
		const buttonElement = target.closest('.convesiopay-btn');
		const button = buttonElement as HTMLButtonElement | null;
		if (button && !button.classList.contains('hide') && !button.disabled) {
			void confirm(event);
		}
	};

	// Attach to document for event delegation (handles dynamically shown/hidden buttons)
	// Check if handler already exists to avoid duplicates
	if (!(document as any).__convesiopayClickHandler) {
		document.addEventListener('click', handleClick);
		(document as any).__convesiopayClickHandler = true;
	}

	// Also try to attach directly to existing buttons (backup for immediate clicks)
	$qsAll<HTMLButtonElement>('.convesiopay-btn', ($el: HTMLButtonElement) => {
		// Remove existing handler if any to avoid duplicates
		$el.removeEventListener('click', handleClick);
		$el.addEventListener('click', async (event: Event) => {
			event.stopPropagation(); // Prevent delegation handler from also firing
			if (!$el.disabled && !$el.classList.contains('hide')) {
				void confirm(event);
			}
		});
	});
}

// Default export for compatibility
export default async function initConvesioPayPaymentIntegrationWithOrderService(orderService: OrderService): Promise<void> {
	await initConvesioPayPaymentIntegration(orderService);
}

